import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2 } from "lucide-react";
import { FadeUp, SectionHeader } from "./Reveal";
import { COURSES, LANGUAGES } from "../../lib/content";
import { detectRegion, STATIC_PRICES } from "../../lib/pricing";
import { useSound } from "../../hooks/useSound";

const scrollTo = (hash) => {
  if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -60, duration: 1.4 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

const useVinylAudio = () => {
  const audioRef = useRef(null);

  const stop = () => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
  };

  const start = (src) => {
    if (!src) return;
    let el = audioRef.current;
    if (!el) {
      el = new Audio();
      el.loop = true;
      el.preload = "auto";
      audioRef.current = el;
    }
    if (el.src !== window.location.origin + src && !el.src.endsWith(src)) {
      el.src = src;
    }
    el.currentTime = 0;
    el.volume = 0.85;
    el.play().catch(() => {});
  };

  useEffect(() => stop, []);
  return { start, stop };
};

const Vinyl = ({ label, playing }) => (
  <div
    aria-hidden
    className={`vinyl-disc absolute top-1/2 -translate-y-1/2 right-0 transition-transform duration-700 ${
      playing
        ? "translate-x-10 sm:translate-x-14 md:translate-x-20"
        : "translate-x-4 sm:translate-x-8 group-hover:translate-x-14 md:group-hover:translate-x-20"
    }`}
  >
    <div
      className="vinyl-spinner w-40 h-40 sm:w-56 sm:h-56 md:w-72 md:h-72 rounded-full relative"
      style={{
        background:
          "repeating-radial-gradient(circle at center, #17120d 0px, #17120d 2px, #2a2118 3px, #17120d 4px)",
        boxShadow: "0 14px 40px rgba(28,22,16,0.4)",
        animation: playing ? "vinyl-spin 2.4s linear infinite" : undefined,
      }}
    >
      <div className="absolute inset-0 m-auto w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-[#DB0909] border-2 border-[#1c1610] flex items-center justify-center text-center">
        <span className="font-mono text-[7px] sm:text-[8px] tracking-[0.15em] uppercase text-[#fdf6ec] px-2 sm:px-3 leading-tight">{label}</span>
      </div>
      <div className="absolute inset-0 m-auto w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-[#f7f6f2] border border-[#1c1610]" />
    </div>
    <div
      className="absolute -top-4 sm:-top-6 right-5 sm:right-8 origin-top transition-transform duration-700"
      style={{ transform: `rotate(${playing ? 22 : -20}deg)` }}
    >
      <div className="w-[2px] sm:w-[3px] h-16 sm:h-24 bg-[#1c1610] mx-auto" />
      <div className="w-2.5 sm:w-3 h-5 sm:h-6 bg-[#950606] border border-[#1c1610] mx-auto -mt-0.5" />
    </div>
  </div>
);

const Meta = ({ meta }) => (
  <div className="grid grid-cols-4 border-y border-[rgba(28,22,16,0.14)] divide-x divide-[rgba(28,22,16,0.14)]">
    {meta.map(([n, label]) => (
      <div key={label} className="py-5 text-center">
        <p className="font-mono font-bold text-xl md:text-2xl text-[#1c1610]">{n}</p>
        <p className="mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-[#4a3e30]">{label}</p>
      </div>
    ))}
  </div>
);

const Needs = ({ needs }) => (
  <div className="flex flex-wrap gap-2">
    {needs.map((n) => (
      <span key={n} className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#4a3e30] border border-[rgba(28,22,16,0.18)] px-3 py-1.5">
        {n}
      </span>
    ))}
  </div>
);

const Sleeve = ({ course, side, playing, onToggle }) => (
  <div className="relative pr-8 sm:pr-16 md:pr-24">
    <Vinyl label={`${course.name.split(" ").slice(0, 3).join(" ")} · ABC`} playing={playing} />
    <div className="relative z-10 aspect-square border border-[rgba(28,22,16,0.2)]">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={course.image}
          alt={course.name}
          className="w-full h-full object-cover object-top transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
          style={{ filter: "contrast(1.12) saturate(0.9) sepia(0.12) brightness(0.9)" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(23,18,13,0.85) 0%, transparent 55%)" }} />
        <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-[#f7f6f2] text-[#1c1610] px-3 py-1.5 border border-[#1c1610]">
          {course.tag}
        </span>
        <div className="absolute bottom-0 inset-x-0 p-6 md:p-8">
          <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#ff6f65] mb-2">ABC Records · Est. 2020 · Side {side}</p>
          <h3 className="font-display font-black text-2xl md:text-4xl text-white leading-[1.05]">{course.name}</h3>
        </div>
      </div>
      <button
        data-testid={`vinyl-play-${course.id}`}
        onClick={onToggle}
        className={`sm:hidden absolute top-14 left-4 z-30 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border transition-colors duration-300 ${
          playing
            ? "bg-[#DB0909] text-[#f7f6f2] border-[#DB0909]"
            : "bg-[#1c1610] text-[#DB0909] border-[#DB0909]/50 vinyl-cta-pulse"
        }`}
      >
        {playing ? <Pause size={11} /> : <Volume2 size={11} className="animate-pulse" />}
        {playing ? "Lift needle" : "Tap to listen"}
      </button>
      <div
        className="absolute -top-8 -right-6 w-24 h-24 rounded-full bg-[#950606] border-2 border-[#1c1610] flex flex-col items-center justify-center rotate-12 z-20 shadow-lg"
        data-testid={`course-price-${course.id}`}
      >
        <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-[#f7f6f2]/80">Investment</span>
        <span className="font-mono font-bold text-lg text-[#f7f6f2]">{course.price}</span>
      </div>
    </div>
  </div>
);

const CourseBlock = ({ course, side, playing, onToggle, onHover }) => {
  const { pluck } = useSound();
  return (
    <article className="relative h-full flex flex-col">
      <div
        className="group"
        data-testid={`vinyl-hover-${course.id}`}
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
      >
        <Sleeve course={course} side={side} playing={playing} onToggle={onToggle} />
      </div>
      <div className="mr-8 sm:mr-16 md:mr-24 bg-[#fefefc] border border-t-0 border-[rgba(28,22,16,0.2)] p-8 md:p-10 flex-1 flex flex-col shadow-[8px_8px_0_rgba(28,22,16,0.15)]">
        <p className="text-[#4a3e30] font-light leading-relaxed">{course.blurb}</p>
        <div className="mt-8"><Meta meta={course.meta} /></div>
        {course.topics ? (
          <>
            <p className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#950606]">Track List — Topics Covered</p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {course.topics.map((t, i) => (
                <li key={t} className="flex items-center gap-3 text-sm text-[#1c1610]/90 font-light">
                  <span className="font-mono text-[10px] text-[#950606]">{String(i + 1).padStart(2, "0")}</span> {t}
                </li>
              ))}
            </ul>
            <p data-testid="course-dropin-note" className="mt-6 border border-[#DB0909]/40 bg-[#DB0909]/[0.04] px-4 py-3 text-xs text-[#4a3e30] font-light leading-relaxed">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#950606]">Add-on · </span>
              Batch already started? Join any individual class above for <span className="text-[#1c1610] font-medium">{course.dropinPrice}/class</span> — as long as spots remain.
            </p>
          </>
        ) : (
          <>
            <p className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#950606]">Track List — Songs You'll Learn</p>
            <ul className="mt-5 space-y-5">
              {course.songs.map((s) => (
                <li key={s.n} className="flex gap-4 items-start">
                  <span className="font-mono text-[#950606] text-sm pt-0.5">{s.n}</span>
                  <div className="flex-1">
                    <p className="text-[#1c1610] text-sm font-medium">{s.title}</p>
                    <p className="text-[#4a3e30] text-xs font-light mt-0.5">{s.by}</p>
                  </div>
                  <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#4a3e30] border border-[rgba(28,22,16,0.18)] px-2 py-1">{s.genre}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="mt-8"><Needs needs={course.needs} /></div>
        <div className="flex-1" />
        <button
          data-testid={`course-cta-${course.id}`}
          onMouseEnter={() => pluck(course.id === "songs" ? 164.8 : 220)}
          onClick={() => {
            window.dispatchEvent(new CustomEvent("preselect-course", { detail: course.id }));
            scrollTo("#contact");
          }}
          className="vibe mt-10 w-full font-mono text-xs tracking-[0.25em] uppercase bg-[#1c1610] text-[#f7f6f2] py-4 hover:bg-[#DB0909] active:bg-[#950606] transition-colors duration-300"
        >
          Reserve Side {side} → {course.price}
        </button>
      </div>
    </article>
  );
};

export const Courses = () => {
  const { start, stop } = useVinylAudio();
  const { enabled: soundOn } = useSound();
  const [playingId, setPlayingId] = useState(null);
  const region = detectRegion();
  const priced = (c) => ({ ...c, price: STATIC_PRICES[region][c.id], dropinPrice: STATIC_PRICES[region].dropin });
  const songs = priced(COURSES.find((c) => c.id === "songs"));
  const indie = priced(COURSES.find((c) => c.id === "indie"));

  useEffect(() => {
    if (!soundOn) stop();
  }, [soundOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => {
    if (playingId === id) {
      stop();
      setPlayingId(null);
    } else {
      const c = COURSES.find((x) => x.id === id);
      if (soundOn) start(c?.vinyl);
      setPlayingId(id);
    }
  };

  const canHover = typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;
  const hoverPlay = (id, on) => {
    if (!canHover) return;
    if (on) {
      const c = COURSES.find((x) => x.id === id);
      if (soundOn) start(c?.vinyl);
      setPlayingId(id);
    } else {
      stop();
      setPlayingId((cur) => (cur === id ? null : cur));
    }
  };

  return (
    <section id="courses" data-testid="courses-section" className="relative py-32 md:py-48 bg-[#eceae4]/70">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <SectionHeader number="02" kicker="The Courses">
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#1c1610]">
            <FadeUp>Choose your <em className="italic text-[#DB0909]">course.</em></FadeUp>
          </h2>
        </SectionHeader>
        <FadeUp delay={0.1} className="mt-6 max-w-md">
          <p className="text-[#4a3e30] font-light leading-relaxed">
            Two live online courses, pressed like records. Hover a sleeve and the needle drops. Pick your side and reserve.
          </p>
        </FadeUp>

        <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-24 lg:gap-x-24">
          <FadeUp className="lg:col-span-6 relative hover:z-40 h-full" data-testid="course-card-songs">
            <CourseBlock course={songs} side="A" playing={playingId === "songs"} onToggle={() => toggle("songs")} onHover={(on) => hoverPlay("songs", on)} />
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-6 relative hover:z-40 h-full" data-testid="course-card-indie">
            <CourseBlock course={indie} side="B" playing={playingId === "indie"} onToggle={() => toggle("indie")} onHover={(on) => hoverPlay("indie", on)} />
          </FadeUp>
        </div>

        <FadeUp className="mt-24 flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-testid="courses-language-strip">
          <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-[#4a3e30] mr-1">Taught in</span>
          {LANGUAGES.map((l) => (
            <span
              key={l}
              className="inline-flex items-center font-mono text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-[#1c1610] border border-[rgba(28,22,16,0.35)] px-3 py-1.5 bg-[#f7f6f2]/60"
            >
              {l}
            </span>
          ))}
        </FadeUp>
      </div>
    </section>
  );
};
